import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import CoilChip from '../CoilChip';
import AttachedFileRow from './AttachedFileRow';
import LinkedPolicyRow from './LinkedPolicyRow';
import { Card, CardContent } from '../../ui/card';
import { cn } from '../../../lib/utils';
import { parseCoils } from '../utils';
import { roomPolicies } from '../roomOverviewUtils';
import {
    SpatialGradientDTO,
    MaxSARDTO,
    labelSarRows,
} from '../../../models/MRIMachineModels';
import { MRIMachinePopulated, CompatibilityAnnotations } from '../types';

interface SpecTableProps {
    machine: MRIMachinePopulated;
    compatibility?: CompatibilityAnnotations;
}

interface SpecRowProps {
    label: string;
    value: React.ReactNode;
    warning?: boolean;
    valueClassName?: string;
}

function SpecRow({ label, value, warning, valueClassName }: SpecRowProps) {
    return (
        <div
            className={cn(
                'grid grid-cols-[2fr_3fr] gap-4 px-4 py-3',
                warning
                    ? 'bg-warning/5'
                    : 'even:bg-muted dark:even:bg-neutral-800'
            )}
        >
            <div className="text-sm font-medium text-subtle-foreground dark:text-neutral-300">
                {label}
            </div>
            <div className={cn('min-w-0 text-sm font-medium', valueClassName)}>
                {value}
            </div>
        </div>
    );
}

export default function SpecTable({ machine, compatibility }: SpecTableProps) {
    const { t } = useTranslation();
    const coils = parseCoils(machine.TRCoils);
    const policies = roomPolicies(machine);

    const gradientExceeds = (sg: SpatialGradientDTO) => {
        if (!compatibility?.isRestricted || !compatibility.gradientAnalysis) {
            return false;
        }
        const result = compatibility.gradientAnalysis.diameterResults.find(
            (dr) => dr.diameter === String(sg.diameter)
        );
        return !!result && !result.isWithinLimit;
    };

    const gradientLabel = (sg: SpatialGradientDTO) => {
        const isNumeric = /^\d+(\.\d+)?$/.test(String(sg.diameter).trim());
        return t('roomOverview.machineDetail.spatialGradient', {
            label: isNumeric ? `${sg.diameter} cm` : sg.diameter,
        });
    };

    return (
        <Card className="border-subtle bg-card">
            <CardContent className="p-0">
                <div className="divide-y divide-subtle">
                    <SpecRow
                        label={t(
                            'roomOverview.machineDetail.staticMagneticField'
                        )}
                        value={`${machine.FieldStrength} Tesla`}
                    />

                    {machine.SpatialGradient?.map(
                        (sg: SpatialGradientDTO & { _id?: string }, index) => {
                            const exceeds = gradientExceeds(sg);
                            return (
                                <SpecRow
                                    key={sg._id || `sg-${index}`}
                                    label={gradientLabel(sg)}
                                    value={`${sg.value} T/m (${Math.round(sg.value * 100)} Gauss/cm)`}
                                    warning={exceeds}
                                    valueClassName={
                                        exceeds ? 'text-warning' : undefined
                                    }
                                />
                            );
                        }
                    )}

                    <SpecRow
                        label={t(
                            'roomOverview.machineDetail.maxSlewRatePerAxis'
                        )}
                        value={`${machine.SlewRatePerAxis} T/m/s`}
                    />
                    <SpecRow
                        label={t(
                            'roomOverview.machineDetail.maxEffectiveSlewRate'
                        )}
                        value={`${machine.MaxSlewRate} T/m/s`}
                    />

                    <SpecRow
                        label={t('roomOverview.machineDetail.maxSAR')}
                        value={
                            <div className="space-y-1">
                                {labelSarRows<MaxSARDTO & { _id?: string }>(
                                    machine.MaxSAR ?? []
                                ).map((sar, index) => (
                                    <div key={sar._id || index}>
                                        • {sar.label && `${sar.label} - `}
                                        WholeBody {sar.wholeBody} W/kg - Head{' '}
                                        {sar.head} W/kg
                                    </div>
                                ))}
                            </div>
                        }
                    />

                    <SpecRow
                        label={t('roomOverview.machineDetail.maxB1rms')}
                        value={
                            machine.MaxB1rms
                                ? `${machine.MaxB1rms}${!isNaN(Number(machine.MaxB1rms)) ? ' µT' : ''}`
                                : 'N/A'
                        }
                    />

                    <SpecRow
                        label={t('roomOverview.machineDetail.trCoils')}
                        warning={!!compatibility?.coilWarning}
                        value={
                            coils.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {coils.map((coil, i) => (
                                        <CoilChip key={i} coil={coil} />
                                    ))}
                                </div>
                            ) : (
                                'N/A'
                            )
                        }
                    />

                    {machine.notes && (
                        <SpecRow
                            label={t('roomOverview.machineDetail.notes')}
                            value={
                                <div
                                    className="text-sm max-w-none [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            machine.notes
                                        ),
                                    }}
                                />
                            }
                        />
                    )}

                    <SpecRow
                        label={t('roomOverview.machineDetail.linkedPolicies')}
                        value={
                            policies.length ? (
                                <div className="space-y-2">
                                    {policies.map(
                                        ({ policy, onRoom, items }) => (
                                            <LinkedPolicyRow
                                                key={policy._id}
                                                policy={policy}
                                                meta={[
                                                    onRoom &&
                                                        t(
                                                            'roomOverview.machineDetail.policyOnRoom'
                                                        ),
                                                    ...items,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            />
                                        )
                                    )}
                                </div>
                            ) : (
                                <span className="text-sm text-subtle-foreground">
                                    {t(
                                        'roomOverview.machineDetail.noPoliciesLinked'
                                    )}
                                </span>
                            )
                        }
                    />

                    <SpecRow
                        label={t('roomOverview.machineDetail.attachedFiles')}
                        value={
                            machine.attachedFiles?.length ? (
                                <div className="space-y-2">
                                    {machine.attachedFiles.map((file) => (
                                        <AttachedFileRow
                                            key={file._id}
                                            file={file}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <span className="text-sm text-subtle-foreground">
                                    {t(
                                        'roomOverview.machineDetail.noFilesAttached'
                                    )}
                                </span>
                            )
                        }
                    />
                </div>
            </CardContent>
        </Card>
    );
}
